import { Brain, Inbox, MessageCircle, Send } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button, Card, Empty, PageHeader, Textarea, cn } from "../components/ui";
import { WaConnectionCard } from "../components/WaConnection";
import { api, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";

const INTENT_TONE: Record<string, string> = { janji_bayar: "bg-emerald-100 text-emerald-800", minta_mundur: "bg-amber-100 text-amber-800", keberatan: "bg-red-100 text-red-800", sudah_bayar: "bg-sky-100 text-sky-800", pertanyaan: "bg-zinc-100 text-zinc-700", lainnya: "bg-zinc-100 text-zinc-700" };
const INTENT_LABEL: Record<string, string> = { janji_bayar: "Janji bayar", minta_mundur: "Minta mundur", keberatan: "Keberatan", sudah_bayar: "Konfirmasi bayar", pertanyaan: "Pertanyaan", lainnya: "Lainnya" };

function Thread({ phone, onSent }: { phone: string; onSent: () => void }) {
  const [t, setT] = useState<any>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const load = useCallback(async () => { try { const { data } = await api.get(`/wa/inbox/${phone}`); setT(data); } catch (e) { toast.error(errMsg(e)); } }, [phone]);
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [t?.messages?.length]);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/wa/inbox/${phone}/send`, { text }); setText(""); toast.success("Masuk antrian kirim"); await load(); onSent(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!t) return <Empty text="Memuat percakapan…" />;
  return (
    <div className="flex flex-col h-[70vh]" data-testid="wa-thread">
      <div className="border-b pb-2 mb-2 text-sm flex flex-wrap items-center gap-2">
        <b data-testid="wa-thread-title">{t.student ? `${t.student.name} (${t.student.internal_id})` : `+${phone}`}</b>
        <span className="text-muted-foreground">+{phone}</span>
        {t.student?.loan_id && <Link to={`/pinjaman/${t.student.loan_id}`} className="text-xs text-primary underline" data-testid="wa-thread-loan-link">Buka pinjaman</Link>}
        {!t.student && <span className="text-xs text-amber-700">Nomor tidak cocok dengan master siswa</span>}
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="wa-thread-messages">
        {t.messages.map((m: any) => (
          <div key={m.id} className={cn("max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm", m.from_me ? "ml-auto bg-emerald-100 dark:bg-emerald-900/40" : "bg-muted")} data-testid={`wa-msg-${m.id}`}>
            <p className="whitespace-pre-wrap">{m.text}</p>
            <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
              <span>{fmtDateTime(m.at)}</span>{m.from_me && <span>· {m.by === "phone" ? "dari HP" : m.by === "system" ? "otomatis" : m.by}</span>}
              {m.intent && m.intent !== "lainnya" && <span className={cn("rounded-full px-1.5", INTENT_TONE[m.intent])} title={m.summary}>{INTENT_LABEL[m.intent]}{m.promise_date ? ` · ${m.promise_date}` : ""}</span>}
            </div>
          </div>
        ))}
        {t.queued.map((q: any) => <div key={q.id} className="ml-auto max-w-[80%] rounded-lg border border-dashed px-3 py-2 text-sm opacity-70" data-testid={`wa-queued-${q.id}`}><p className="whitespace-pre-wrap">{q.text}</p><p className="text-[10px] mt-1">{q.status === "failed" ? `Gagal: ${q.error}` : "Menunggu antrian kirim…"}</p></div>)}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-2">
        <Textarea data-testid="wa-reply-input" className="min-h-[44px] flex-1" placeholder="Tulis balasan…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button type="button" disabled={busy || !text.trim()} onClick={send} data-testid="wa-reply-send"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function Signals({ rows }: { rows: any[] }) {
  if (!rows.length) return <Empty text="Belum ada sinyal dari balasan siswa. Setiap pesan masuk dari nomor yang cocok dengan master siswa dianalisis AI otomatis." />;
  return (
    <div className="overflow-x-auto">
      <table className="yt-table text-sm" data-testid="wa-signals-table">
        <thead><tr><th>Siswa</th><th>Sinyal</th><th>Janji tgl</th><th>Δ Risiko</th><th>Ringkasan AI</th><th>Waktu</th><th></th></tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.student_id} data-testid={`wa-signal-${s.student_id}`}>
              <td className="font-medium">{s.student?.name || "-"} <span className="text-xs text-muted-foreground">{s.student?.internal_id}</span></td>
              <td><span className={cn("rounded-full px-2 py-0.5 text-xs", INTENT_TONE[s.intent])}>{s.intent_label}</span></td>
              <td>{s.promise_date || "-"}</td>
              <td className={cn("font-mono", s.risk_delta > 0 ? "text-red-600" : "text-emerald-700")}>{s.risk_delta > 0 ? "+" : ""}{s.risk_delta}</td>
              <td className="max-w-md text-xs">{s.summary}</td>
              <td className="text-xs whitespace-nowrap">{fmtDateTime(s.at)}</td>
              <td>{s.loan_id && <Link to={`/pinjaman/${s.loan_id}`} className="text-xs text-primary underline">Pinjaman</Link>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function WhatsApp() {
  const [gw, setGw] = useState<any>(null);
  const [inbox, setInbox] = useState<any[]>([]);
  const [signals, setSignals] = useState<any[]>([]);
  const [tab, setTab] = useState<"inbox" | "signals">("inbox");
  const [active, setActive] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([api.get("/wa/gateway"), api.get("/wa/inbox"), api.get("/wa/signals")]);
      setGw(a.data); setInbox(b.data); setSignals(c.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, [load]);
  const list = inbox.filter((c) => !q || `${c.student?.name || ""} ${c.student?.internal_id || ""} ${c.phone} ${c.push_name}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="whatsapp-page">
      <PageHeader title="WhatsApp Resmi" subtitle="Satu nomor resmi koperasi: tangkap chat siswa, kirim tagihan/pengumuman otomatis, dan analisis AI (janji bayar, minta mundur, keberatan) ke skor risiko." />
      <div className="grid lg:grid-cols-[340px_1fr] gap-4">
        <div className="space-y-4">
          <WaConnectionCard gw={gw} reload={load} />
          <Card title={<span className="inline-flex items-center gap-2"><Inbox className="h-4 w-4" /> Percakapan {gw?.unread ? <span className="rounded-full bg-red-600 text-white text-[10px] px-1.5" data-testid="wa-unread-badge">{gw.unread}</span> : null}</span>} testId="wa-inbox-card">
            <input className="yt-input mb-2" placeholder="Cari nama / ID / nomor" value={q} onChange={(e) => setQ(e.target.value)} data-testid="wa-inbox-search" />
            <div className="max-h-[52vh] overflow-y-auto divide-y" data-testid="wa-inbox-list">
              {!list.length && <p className="text-xs text-muted-foreground py-4 text-center">Belum ada percakapan masuk.</p>}
              {list.map((c) => (
                <button key={c.phone} type="button" onClick={() => { setActive(c.phone); setTab("inbox"); }} className={cn("w-full text-left py-2 px-1 hover:bg-muted/60 rounded", active === c.phone && "bg-muted")} data-testid={`wa-conv-${c.phone}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{c.student?.name || c.push_name || `+${c.phone}`}</span>
                    {c.unread > 0 && <span className="rounded-full bg-emerald-600 text-white text-[10px] px-1.5">{c.unread}</span>}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="truncate">{c.last_from_me ? "Anda: " : ""}{c.last_text}</span>
                    {c.signal && <span className={cn("rounded-full px-1.5 shrink-0", INTENT_TONE[c.signal.intent])}>{INTENT_LABEL[c.signal.intent]}</span>}
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>
        <Card testId="wa-main-card" title={
          <div className="flex gap-2">
            <button type="button" className={cn("text-sm px-3 py-1 rounded-full", tab === "inbox" ? "bg-primary text-primary-foreground" : "bg-muted")} onClick={() => setTab("inbox")} data-testid="wa-tab-inbox"><MessageCircle className="inline h-3.5 w-3.5 mr-1" />Chat</button>
            <button type="button" className={cn("text-sm px-3 py-1 rounded-full", tab === "signals" ? "bg-primary text-primary-foreground" : "bg-muted")} onClick={() => setTab("signals")} data-testid="wa-tab-signals"><Brain className="inline h-3.5 w-3.5 mr-1" />Sinyal AI ({signals.length})</button>
          </div>}>
          {tab === "signals" ? <Signals rows={signals} /> : active ? <Thread phone={active} onSent={load} /> : <Empty text="Pilih percakapan di kiri. Pesan masuk dari siswa akan muncul otomatis setelah nomor resmi terhubung." />}
        </Card>
      </div>
    </div>
  );
}
